import React, { useState } from 'react';
import { RankingComparison, RankingComparisonRequest } from '../services/api';

interface ComparisonViewProps {
  comparisons: RankingComparison[];
  loading?: boolean;
  onSkuClick?: (sku: string) => void;
  onSortChange?: (sortBy: RankingComparisonRequest['sort_by']) => void;
  currentSort?: RankingComparisonRequest['sort_by'];
}

const ComparisonView: React.FC<ComparisonViewProps> = ({
  comparisons,
  loading = false,
  onSkuClick,
  onSortChange,
  currentSort = 'position2'
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [movementFilter, setMovementFilter] = useState<string>('all');

  const formatNumber = (value: number | null, decimals = 2): string => {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString('pt-PT', { 
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals 
    });
  };

  const getPenaltyColorClass = (penalty: number | null): string => {
    if (penalty === null || penalty === undefined) return '';
    if (penalty > 1.0) return 'bg-success text-white';
    if (penalty < 0.8) return 'bg-danger text-white';
    return 'bg-warning';
  };

  const getScoreChangeIcon = (scoreChange: number) => {
    if (scoreChange > 0.01) {
      return <i className="bi bi-arrow-up text-success" title={`+${formatNumber(scoreChange, 4)}`}></i>;
    } else if (scoreChange < -0.01) {
      return <i className="bi bi-arrow-down text-danger" title={formatNumber(scoreChange, 4)}></i>;
    } else {
      return <i className="bi bi-dash text-muted" title="Score estável"></i>;
    }
  };

  const getPenaltyChangeIcon = (penaltyChange: number) => {
    if (penaltyChange > 0.01) {
      return <i className="bi bi-exclamation-triangle text-warning" title={`Penalização aumentou ${formatNumber(penaltyChange, 2)}`}></i>;
    } else if (penaltyChange < -0.01) {
      return <i className="bi bi-check-circle text-success" title={`Penalização diminuiu ${formatNumber(Math.abs(penaltyChange), 2)}`}></i>;
    } else {
      return <i className="bi bi-dash text-muted" title="Penalização estável"></i>;
    }
  };

  const filteredComparisons = comparisons.filter(comparison => {
    if (movementFilter === 'all') return true;
    return comparison.changes.movement_type === movementFilter;
  });

  const movementCounts = {
    all: comparisons.length,
    up: comparisons.filter(c => c.changes.movement_type === 'up').length,
    down: comparisons.filter(c => c.changes.movement_type === 'down').length,
    stable: comparisons.filter(c => c.changes.movement_type === 'stable').length,
    new: comparisons.filter(c => c.changes.movement_type === 'new').length,
    disappeared: comparisons.filter(c => c.changes.movement_type === 'disappeared').length
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">A carregar comparação...</span>
        </div>
      </div>
    );
  }

  if (comparisons.length === 0) {
    return (
      <div className="alert alert-info">
        Nenhuma comparação disponível. Seleccione dois snapshots para comparar.
      </div>
    );
  }

  return (
    <div>
      {/* Controls */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="btn-group" role="group">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowFilters(!showFilters)}
            >
              <i className="bi bi-funnel me-1"></i>
              Filtros
            </button>
            {onSortChange && (
              <div className="dropdown">
                <button 
                  className="btn btn-sm btn-outline-secondary dropdown-toggle" 
                  type="button" 
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Ordenar
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <button 
                      className={`dropdown-item ${currentSort === 'position1' ? 'active' : ''}`}
                      onClick={() => onSortChange('position1')}
                    >
                      Por posição base
                    </button>
                  </li>
                  <li>
                    <button 
                      className={`dropdown-item ${currentSort === 'position2' ? 'active' : ''}`}
                      onClick={() => onSortChange('position2')}
                    >
                      Por posição atual
                    </button>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button 
                      className={`dropdown-item ${currentSort === 'position_change' ? 'active' : ''}`}
                      onClick={() => onSortChange('position_change')}
                    >
                      Maiores subidas
                    </button>
                  </li>
                  <li>
                    <button 
                      className={`dropdown-item ${currentSort === 'position_change_desc' ? 'active' : ''}`}
                      onClick={() => onSortChange('position_change_desc')}
                    >
                      Maiores descidas
                    </button>
                  </li>
                  <li>
                    <button 
                      className={`dropdown-item ${currentSort === 'score_change' ? 'active' : ''}`}
                      onClick={() => onSortChange('score_change')}
                    >
                      Maior aumento de score
                    </button>
                  </li>
                  <li>
                    <button 
                      className={`dropdown-item ${currentSort === 'penalty_change' ? 'active' : ''}`}
                      onClick={() => onSortChange('penalty_change')}
                    >
                      Maior aumento de penalização
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="col-md-6 text-end">
          <small className="text-muted">
            {filteredComparisons.length} de {comparisons.length} produtos
          </small>
        </div>
      </div>

      {/* Movement filter pills */}
      {showFilters && (
        <div className="mb-3">
          <div className="d-flex flex-wrap gap-2">
            {Object.entries(movementCounts).map(([movement, count]) => (
              <button
                key={movement}
                className={`btn btn-sm ${movementFilter === movement ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setMovementFilter(movement)}
              >
                {movement === 'all' && 'Todos'}
                {movement === 'up' && 'Subidas'}
                {movement === 'down' && 'Descidas'}
                {movement === 'stable' && 'Estáveis'}
                {movement === 'new' && 'Novos'}
                {movement === 'disappeared' && 'Saíram'}
                {' '}
                <span className="badge bg-light text-dark">{count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Comparison table */}
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th>SKU</th>
              <th>Produto</th>
              <th>Posição Base</th>
              <th>Posição Atual</th>
              <th>Mudança</th>
              <th>Score Base</th>
              <th>Score Atual</th>
              <th>Δ Score</th>
              <th>Penalização Base</th>
              <th>Penalização Atual</th>
              <th>Δ Penalização</th>
              <th>Categoria</th>
            </tr>
          </thead>
          <tbody>
            {filteredComparisons.map((comparison) => (
              <tr key={comparison.sku}>
                <td>
                  <span 
                    className={onSkuClick ? "text-primary text-decoration-underline cursor-pointer" : ""}
                    onClick={() => onSkuClick?.(comparison.sku)}
                    role={onSkuClick ? "button" : undefined}
                  >
                    {comparison.sku}
                  </span>
                </td>
                <td>
                  <div 
                    className="text-truncate" 
                    style={{ maxWidth: '300px' }}
                    title={comparison.product_name || '-'}
                  >
                    {comparison.product_name || '-'}
                  </div>
                </td>
                <td>
                  <span className="fw-bold">
                    {comparison.snapshot1.position ? `#${comparison.snapshot1.position}` : '-'}
                  </span>
                </td>
                <td>
                  <span className="fw-bold">
                    {comparison.snapshot2.position ? `#${comparison.snapshot2.position}` : '-'}
                  </span>
                </td>
                <td>
                  {comparison.changes.position_change !== 0 && (
                    <span className={comparison.changes.position_change < 0 ? "text-success" : "text-danger"}>
                      {comparison.changes.position_change > 0 ? '+' : ''}{comparison.changes.position_change}
                    </span>
                  )}
                </td>
                <td>
                  {formatNumber(comparison.snapshot1.score, 4)}
                </td>
                <td>
                  <div className="d-flex align-items-center">
                    {formatNumber(comparison.snapshot2.score, 4)}
                    {getScoreChangeIcon(comparison.changes.score_change)}
                  </div>
                </td>
                <td>
                  <span className={comparison.changes.score_change > 0 ? "text-success" : comparison.changes.score_change < 0 ? "text-danger" : ""}>
                    {comparison.changes.score_change > 0 ? '+' : ''}{formatNumber(comparison.changes.score_change, 4)}
                  </span>
                </td>
                <td>
                  <span className={`badge ${getPenaltyColorClass(comparison.snapshot1.penalty)}`}>
                    {formatNumber(comparison.snapshot1.penalty, 2)}
                  </span>
                </td>
                <td>
                  <span className={`badge ${getPenaltyColorClass(comparison.snapshot2.penalty)}`}>
                    {formatNumber(comparison.snapshot2.penalty, 2)}
                  </span>
                  <span className="ms-1">
                    {getPenaltyChangeIcon(comparison.changes.penalty_change)}
                  </span>
                </td>
                <td>
                  <span className={comparison.changes.penalty_change > 0 ? "text-warning" : comparison.changes.penalty_change < 0 ? "text-success" : ""}>
                    {comparison.changes.penalty_change > 0 ? '+' : ''}{formatNumber(comparison.changes.penalty_change, 2)}
                  </span>
                </td>
                <td>
                  <small className="text-muted">
                    {comparison.category || '-'}
                  </small>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredComparisons.length === 0 && movementFilter !== 'all' && (
        <div className="alert alert-info">
          Nenhum produto encontrado com o filtro "{movementFilter}".
        </div>
      )}
    </div>
  );
};

export default ComparisonView;